import apiBase from '../utils/baseApi';

export interface GenerationRequest {
  prompt: string;
  musicStyle?: string;
  lyrics?: string;
  thumbnailUrl?: string;
  userPhotoUrl?: string;
  uniqueId?: string;
  voiceId?: string;
  artistTag?: string;
  language?: string;
  gender?: string;
  additionalData?: string;
  useV2Api?: boolean;
  stationMode?: boolean; // Station (conference demo) flow — API skips content moderation when true
  numOutputs?: number; // 1 or 2 (default 2)
}

export interface GenerationResponse {
  taskId: string;
  status:
    | 'pending'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'thumbnail1_ready'
    | 'thumbnail2_ready'
    | 'thumbnails_ready'
    | 'lyrics_ready'
    | 'producing';
  prompt: string;
  musicStyle?: string;
  lyrics?: string;
  // Song 1
  songUrl1?: string;
  title1?: string;
  lyrics1?: string;
  lyricsTimestamped1?: string;
  duration1?: number;
  // Song 2
  songUrl2?: string;
  title2?: string;
  lyrics2?: string;
  lyricsTimestamped2?: string;
  duration2?: number;
  // Cover images
  thumbnailUrl1?: string;
  thumbnailUrl2?: string;
  musicGptCoverUrl?: string;
  musicGptCoverThumbnail?: string;
  linkedTaskId?: string;
  conversionId1?: string;
  conversionId2?: string;
  presentationUrl?: string;
  businessPlanUrl?: string;
  webpageUrl?: string;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export interface StartGenerationResponse {
  taskId: string;
  status: string;
  prompt: string;
  musicStyle?: string;
  message: string;
  lyrics?: string;
}

export const createGeneration = async (
  data: GenerationRequest,
): Promise<StartGenerationResponse> => {
  try {
    const response = await apiBase.post<StartGenerationResponse>(
      '/api/generations',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error creating generation:', error);
    throw error;
  }
};

export const getGenerationStatus = async (
  taskId: string,
): Promise<GenerationResponse> => {
  try {
    const response = await apiBase.get<GenerationResponse>(
      `/api/generations/${taskId}`,
    );
    return response.data;
  } catch (error: any) {
    // Handle 403 Forbidden - Permission Denied
    if (error.response?.status === 403) {
      console.error('Permission denied for generation:', taskId);
      // Redirect to access denied page
      window.location.href = '/access-denied';
      // Throw error to stop any further processing
      throw new Error('Access Denied');
    }
    console.error('Error fetching generation status:', error);
    throw error;
  }
};

export const pollGenerationStatus = async (
  taskId: string,
  onProgress?: (status: GenerationResponse) => void,
  maxAttempts: number = 120,
  pollInterval: number = 5000,
): Promise<GenerationResponse> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    const status = await getGenerationStatus(taskId);

    if (onProgress) {
      onProgress(status);
    }

    if (status.status === 'completed' || status.status === 'lyrics_ready') {
      return status;
    }

    if (status.status === 'failed') {
      throw new Error(status.error || 'Generation failed');
    }
  }

  throw new Error('Generation timeout - max polling attempts reached');
};

// Produce songs from lyrics
export const produceSongs = async (
  taskId: string,
  lyrics?: string,
): Promise<StartGenerationResponse> => {
  try {
    const response = await apiBase.post<StartGenerationResponse>(
      `/api/generations/${taskId}/produce`,
      lyrics ? { lyrics } : {},
    );
    return response.data;
  } catch (error) {
    console.error('Error producing songs:', error);
    throw error;
  }
};

/**
 * Generate or regenerate album covers for a music generation task.
 *
 * Despite the name "regenerate", this endpoint handles both first-time and
 * subsequent cover generation. Server wraps the prompt with buildImagePrompt()
 * and enforces album cover guardrails.
 *
 * Use `modificationRequest` (not `prompt`) when the user wants to modify an
 * existing cover (e.g. "change chair to piano"). The server then applies only
 * the requested change while preserving identity, composition, and styling
 * from the reference image.
 */
export const regenerateCovers = async (
  taskId: string,
  params?: {
    imageUrl?: string;
    prompt?: string;
    /**
     * Modification-only request (e.g. "change chair to piano"). Triggers the
     * server's modification-specific prompt template that preserves the
     * reference image and applies only the requested change.
     */
    modificationRequest?: string;
  },
): Promise<StartGenerationResponse> => {
  const response = await apiBase.post<StartGenerationResponse>(
    `/api/generations/${taskId}/covers`,
    params || {},
  );
  return response.data;
};

// regenerate lyrics for a generation
export const regenerateLyrics = async (
  taskId: string,
  params?: { prompt?: string; instructions?: string },
): Promise<StartGenerationResponse> => {
  const response = await apiBase.post<StartGenerationResponse>(
    `/api/generations/${taskId}/lyrics`,
    params || {},
  );
  return response.data;
};

export const regenerateTitle = async (
  taskId: string,
  params: { modification_request: string },
): Promise<StartGenerationResponse> => {
  const response = await apiBase.post<StartGenerationResponse>(
    `/api/generations/${taskId}/title`,
    params,
  );
  return response.data;
};

// Image generation types and functions
export interface ImageGenerationRequest {
  prompt: string;
  imageUrl?: string;
  imageUrls?: string[];
  type?: 'album' | 'design';
  stationMode?: boolean;
}

export interface ImageGenerationResponse {
  status: string;
  imageUrl: string;
}

export const createImage = async (
  data: ImageGenerationRequest,
): Promise<ImageGenerationResponse> => {
  try {
    const response = await apiBase.post<ImageGenerationResponse>(
      '/api/generations/images',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error creating image:', error);
    throw error;
  }
};

export interface CreatePresentationRequest {
  topic: string;
  uniqueId?: string;
}

export interface RegeneratePresentationRequest {
  taskId: string;
  prompt: string;
}

export interface PresentationResponse {
  status: string;
  taskId: string;
  presentationUrl: string;
}

export const createPresentation = async (
  data: CreatePresentationRequest,
): Promise<PresentationResponse> => {
  try {
    const response = await apiBase.post<PresentationResponse>(
      '/api/generations/presentations',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error creating presentation:', error);
    throw error;
  }
};

export const regeneratePresentation = async (
  data: RegeneratePresentationRequest,
): Promise<PresentationResponse> => {
  try {
    const response = await apiBase.post<PresentationResponse>(
      '/api/generations/presentations/regenerate',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error regenerating presentation:', error);
    throw error;
  }
};

export interface CreateBusinessPlanRequest {
  topic: string;
  uniqueId?: string;
}

export interface RegenerateBusinessPlanRequest {
  taskId: string;
  prompt: string;
}

export interface BusinessPlanResponse {
  status: string;
  taskId: string;
  businessPlanUrl: string;
}

export const createBusinessPlan = async (
  data: CreateBusinessPlanRequest,
): Promise<BusinessPlanResponse> => {
  try {
    const response = await apiBase.post<BusinessPlanResponse>(
      '/api/generations/business-plans',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error creating business plan:', error);
    throw error;
  }
};

export const regenerateBusinessPlan = async (
  data: RegenerateBusinessPlanRequest,
): Promise<BusinessPlanResponse> => {
  try {
    const response = await apiBase.post<BusinessPlanResponse>(
      '/api/generations/business-plans/regenerate',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error regenerating business plan:', error);
    throw error;
  }
};

export interface CreateWebpageRequest {
  topic: string;
  uniqueId?: string;
}

export interface RegenerateWebpageRequest {
  taskId: string;
  prompt: string;
}

export interface WebpageResponse {
  status: string;
  taskId: string;
  webpageUrl: string;
}

export const createWebpage = async (
  data: CreateWebpageRequest,
): Promise<WebpageResponse> => {
  try {
    const response = await apiBase.post<WebpageResponse>(
      '/api/generations/webpages',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error creating webpage:', error);
    throw error;
  }
};

export const regenerateWebpage = async (
  data: RegenerateWebpageRequest,
): Promise<WebpageResponse> => {
  try {
    const response = await apiBase.post<WebpageResponse>(
      '/api/generations/webpages/regenerate',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error regenerating webpage:', error);
    throw error;
  }
};

export interface UpdateSavedCreationIdRequest {
  songVersion: 1 | 2;
  savedCreationId: string;
}

export const updateSavedCreationId = async (
  taskId: string,
  data: UpdateSavedCreationIdRequest,
): Promise<{ success: boolean }> => {
  try {
    const response = await apiBase.patch<{ success: boolean }>(
      `/api/generations/${taskId}/saved-creation-ids`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error updating saved creation ID:', error);
    throw error;
  }
};
